/** Display types for scheduled-task instruction sections. Mirrors papaya; derived from description on read. */

export type InstructionItem = {
  text: string
  severity?: 'flag'
}

export type InstructionSection =
  | { type: 'paragraph'; title?: string; text: string }
  | {
      type: 'checklist'
      title?: string
      style?: 'numbered' | 'lettered' | 'bullet'
      items: InstructionItem[]
    }
  | {
      type: 'context'
      title?: string
      startDate?: string
      endDate?: string
      text?: string
      items?: InstructionItem[]
    }
  | { type: 'guardrails'; title?: string; items: InstructionItem[] }
  | { type: 'output_format'; title?: string; items: InstructionItem[] }
  | { type: 'technical'; title?: string; items: InstructionItem[] }

export type InstructionDocument = {
  version: 1
  sections: InstructionSection[]
}

const SECTION_TYPES = new Set([
  'paragraph',
  'checklist',
  'context',
  'guardrails',
  'output_format',
  'technical',
])

export function toSentenceCase(title: string): string {
  const trimmed = title.trim()
  if (!trimmed) return trimmed
  const lower = trimmed.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

export function coerceInstructionDocument(raw: unknown): InstructionDocument | null {
  if (raw == null) return null
  let value = raw
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw)
    } catch {
      return null
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const sections = (value as { sections?: unknown }).sections
  if (!Array.isArray(sections) || sections.length === 0) return null
  if (!sections.every((s) => s && typeof s === 'object' && SECTION_TYPES.has((s as { type?: string }).type || ''))) {
    return null
  }
  return value as InstructionDocument
}

export function partitionInstructionSections(sections: InstructionSection[]): {
  visible: InstructionSection[]
  technical: InstructionSection[]
} {
  const visible: InstructionSection[] = []
  const technical: InstructionSection[] = []
  for (const section of sections) {
    if (section.type === 'technical') technical.push(section)
    else visible.push(section)
  }
  return { visible, technical }
}
